<script lang="ts" setup>
import { computed, ref, watch, markRaw, onMounted, onUnmounted } from 'vue';
import { Avatar, IcArrowRight, ErrorOutlinedIcon } from '@xpos/icons-svg';
import PasswordInput from './PasswordInput.vue';
import { useEmployeeStore } from '@/stores/employee';
import { storeToRefs } from 'pinia';
import { Employee } from '@posx/core';
import { useRouter } from 'vue-router';
import { ElMessageBox } from 'element-plus';
import { notify } from '@/utils/notification';
import * as Sentry from '@sentry/vue';
import { useI18n } from 'vue-i18n';
import { cleanupSessionData } from '@/utils/cleanup';
import { eventBus } from '@/libs/EventBus';
import { getPlatform } from '@/utils/misc';

const { t } = useI18n();
const employeeStore = useEmployeeStore();
const { list: staffs, currentStaff } = storeToRefs(employeeStore);

onMounted(() => employeeStore.startLiveQuery());
onUnmounted(() => employeeStore.stopLiveQuery());

const $emit = defineEmits(['change', 'update:modelValue', 'close']);
const $props = defineProps({
  modelValue: {
    type: Boolean,
  },
});

const chooseStaff = ref<Employee>();
const visible = ref($props.modelValue);
const step = ref(1);
const password = ref('');
const router = useRouter();
const otherStaffs = computed(() => {
  return staffs.value.filter((item) => item.uid !== currentStaff.value?.uid && item.can_login !== false);
});

// 检查是否为移动端
const isMobile = computed(() => {
  return getPlatform() === 'mobile-web';
});

watch(
  () => $props.modelValue,
  (newVal) => {
    visible.value = newVal;
  },
);

const handleKeyboardChange = async (val: string) => {
  if (val === '.') {
    return;
  }
  if (val === 'backspace') {
    password.value = password.value.substring(0, password.value.length - 1);
  } else if (password.value.length !== 4) {
    password.value += val;
  }
  if (password.value.length === 4) {
    try {
      const result = await employeeStore.login(chooseStaff.value!.uid, password.value);
      if (result) {
        await employeeStore.setEmployee(chooseStaff.value!);
        if (chooseStaff.value?.language) eventBus.emit('localeChange', chooseStaff.value?.language);
        notify.success(t('common.loginSuccess'));
        router.push('/');
        handleClose();
      }
    } catch (e: any) {
      Sentry.captureException(e);
      notify.error(e.message);
    } finally {
      password.value = '';
    }
  }
};

const handleLogout = () => {
  ElMessageBox.confirm(t('account.deleteConfirm.title'), 'Error', {
    confirmButtonText: t('account.deleteConfirm.ok'),
    cancelButtonText: t('account.deleteConfirm.cancel'),
    type: 'error',
    icon: markRaw(ErrorOutlinedIcon),
    autofocus: false,
  }).then(async () => {
    employeeStore.logout();
    await cleanupSessionData();
    router.push('/login');
  }).catch(e => { if (e !== 'cancel') Sentry.captureException(e); });
};

const handleClose = () => {
  step.value = 1;
  password.value = '';
  $emit('change', false);
  $emit('update:modelValue', false);
  $emit('close', false);
};
</script>

<template>
    <x-dialog v-model="visible" :width="isMobile ? '90%' : '770px'" :title="t('account.switchAccountTitle')"
    :before-close="handleClose" @close="handleClose" :close-on-click-modal="false">
    <div class="w-[350px] h-full flex flex-col" v-if="step === 1">
      <div class="flex-grow flex flex-col overflow-hidden">
        <div class="text-[20px] mb-[10px]" v-if="currentStaff">
          {{ t('account.currentAccount') }}
        </div>
        <div v-if="currentStaff" class="mb-[20px]">
          <x-item :border="true" class="border-2 !border-[#535998]">
            <template #start>
              <img src="" class="" v-if="false" />
              <el-icon size="25" color="#A8A8A8" class="align-middle">
                <Avatar />
              </el-icon>
            </template>
            {{ currentStaff?.name }}
          </x-item>
        </div>
        <div class="text-[20px] mb-[10px]">{{ t('account.switchTo') }}</div>
        <div class="overflow-y-auto scrollbar-none max-h-[330px]">
          <x-item border class="mb-[10px]" @click="(chooseStaff = item), (step = 2)" v-for="item in otherStaffs">
            <template #start>
              <img src="" class="" v-if="false" />
              <el-icon size="25" color="#A8A8A8">
                <Avatar />
              </el-icon>
            </template>
            {{ item.name }}
            <template #end>
              <el-icon color="#3A3A3A" size="30">
                <IcArrowRight />
              </el-icon>
            </template>
          </x-item>
        </div>
      </div>
      <el-button color="#E53030" class="w-full mt-auto" @click="handleLogout">{{ t('common.logout')
        }}</el-button>
      <!-- <div>
        <div class="text-[20px] mb-[10px]">Staff Shift</div>
        <el-row :gutter="10">
          <el-col :span="12">
            <el-button class="w-full" size="small">Open</el-button>
          </el-col>
          <el-col :span="12"
            ><el-button
              type="primary"
              class="w-full"
              @click="handleClose"
              size="small"
              >Close</el-button
            ></el-col
          >
        </el-row>
      </div> -->
    </div>
    <div v-else class="w-[350px] flex flex-col justify-between">
      <div class="text-center">
        <img src="" class="w-[100px] h-[100px] rounded-full" v-if="false" />
        <el-icon size="100" color="#A8A8A8">
          <Avatar />
        </el-icon>
        <div class="text-2xl mt-[60px]">{{ chooseStaff?.name }}</div>
      </div>
      <div>
        <PasswordInput :length="4" v-model="password" />
      </div>
      <div>
        <x-keyboard @change="handleKeyboardChange" :single="true" />
      </div>
    </div>
  </x-dialog>
</template>

<style scoped>
.scrollbar-none::-webkit-scrollbar {
  display: none;
}
.scrollbar-none {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
